import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useAuthStore } from '../../stores/root_store';
import {
  jobApplicationApi,
  type ApplicationDetail,
  type ApplicationOutcome,
} from '../../api/job_application_api';
import { SafeHtml } from '../../components/SafeHtml';
import { APTITUDE_QUESTIONS } from '../apply/questions';
import { LikelyChip, OutcomeChip } from './ManagerApplicationsPage';

function Check({ ok, label }: { ok: boolean; label: string }) {
  return (
    <ListItem disableGutters dense>
      <ListItemIcon sx={{ minWidth: 32 }}>
        {ok ? <CheckCircleIcon color="success" fontSize="small" /> : <CancelIcon color="error" fontSize="small" />}
      </ListItemIcon>
      <ListItemText primary={label} />
    </ListItem>
  );
}

function Resume({ text }: { text: string | null }) {
  if (!text) return <Typography color="text.secondary">No resume provided.</Typography>;
  // Legacy applications stored rich-text HTML; new ones are plain text
  if (/<[a-z][\s\S]*>/i.test(text)) return <SafeHtml content={text} />;
  return <Typography sx={{ whiteSpace: 'pre-wrap' }}>{text}</Typography>;
}

export const ManagerApplicationDetail = observer(() => {
  const authStore = useAuthStore();
  const navigate = useNavigate();
  const { sampleUuid } = useParams<{ sampleUuid: string }>();

  const [detail, setDetail] = useState<ApplicationDetail | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState<ApplicationOutcome | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canView = authStore.roles.includes('manager') || !!authStore.user?.is_manager;

  const load = useCallback(async () => {
    if (!sampleUuid) return;
    try {
      const d = await jobApplicationApi.detail(sampleUuid);
      setDetail(d);
      setNotes(d.sample.outcome_notes ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load application');
    }
  }, [sampleUuid]);

  useEffect(() => {
    if (canView) load();
  }, [canView, load]);

  const decide = async (outcome: ApplicationOutcome) => {
    if (!sampleUuid) return;
    setSaving(outcome);
    setError(null);
    setSaved(null);
    try {
      const d = await jobApplicationApi.setOutcome(sampleUuid, outcome, notes);
      setDetail(d);
      const name = d.sample.applicant?.name ?? 'The applicant';
      setSaved(outcome === 'accept' ? `${name} is now an assistant.` : `${name}'s application was marked failed.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the decision');
    } finally {
      setSaving(null);
    }
  };

  if (authStore.initialized && !canView) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">You do not have permission to access this page.</Alert>
      </Container>
    );
  }

  const back = (
    <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/manager/applications')} sx={{ mb: 2 }}>
      All applications
    </Button>
  );

  if (!detail) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {back}
        {error ? <Alert severity="error">{error}</Alert> : (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
        )}
      </Container>
    );
  }

  const { sample, survey } = detail;
  const applicant = sample.applicant;
  const firstName = applicant?.name.split(' ')[0] ?? 'this applicant';

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {back}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="h4">{applicant?.name ?? 'Unknown applicant'}</Typography>
        <OutcomeChip outcome={sample.outcome} />
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {[applicant?.email, [applicant?.home_city, applicant?.home_state].filter(Boolean).join(', ')]
          .filter(Boolean).join(' · ')}
        {' · applied '}
        {new Date(sample.date_created).toLocaleDateString()}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Sample task reply</Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                {sample.message || 'No reply submitted.'}
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Resume</Typography>
              <Resume text={survey?.resume ?? null} />
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Decision</Typography>
              {saved && <Alert severity="success" sx={{ mb: 2 }}>{saved}</Alert>}
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <TextField
                label="Notes (internal)"
                fullWidth
                multiline
                minRows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Stack direction="row" spacing={1}>
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  startIcon={saving === 'accept' ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                  disabled={!!saving}
                  onClick={() => decide('accept')}
                >
                  Accept
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={saving === 'failed' ? <CircularProgress size={16} color="inherit" /> : <CancelIcon />}
                  disabled={!!saving}
                  onClick={() => decide('failed')}
                >
                  Fail
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Accepting makes {firstName} an assistant.
                {sample.date_accepted && ` Accepted ${new Date(sample.date_accepted).toLocaleDateString()}.`}
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Screening</Typography>
                <LikelyChip likely={sample.likely_outcome} />
              </Stack>
              {survey ? (
                <>
                  <List dense>
                    <Check ok={survey.legally_able_to_work === 'Yes'} label="Legally able to work in the US" />
                    <Check
                      ok={survey.their_score === survey.their_score_max}
                      label={`Grammar: ${Math.max(survey.their_score, 0)} of ${survey.their_score_max}`}
                    />
                    {survey.auto_denied && <Check ok={false} label="Auto-denied (location or eligibility)" />}
                  </List>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">Aptitude</Typography>
                  <List dense>
                    {APTITUDE_QUESTIONS.map((q) => (
                      <Check key={q.field} ok={!!survey.aptitude[q.field]} label={q.label} />
                    ))}
                  </List>
                </>
              ) : (
                <Typography color="text.secondary" sx={{ mt: 1 }}>No survey on file.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
});
